<template>
  <div class="shipment-map-container">
    <Loader
      v-if="isLoading"
      :is-loading="isLoading"
      color="blue"
      is-linear
    />
    <div
      v-else-if="!origin && !destination && locations.length === 0"
      class="no-data-message"
    >
      {{ shipment.trackingRef || 'Shipment' }} has no locations
    </div>
    <MapWithPopup
      v-else
      :locations="locations.map((location) => ({
        ...location,
        properties: {
          id: shipment.id,
          name: shipment.trackingRef,
          timestamp: location.timestamp,
          address: location.address,
        },
      }))"
      :origin="origin"
      :destination="destination"
      :alerts="alerts"
      :has-more-locations="isMoreData"
    />
  </div>
</template>
<script>
import MapWithPopup from '../../molecules/map/map-with-popup';
import Loader from '../../atoms/loader/loader';
import {
  mapItemTypes, mapEntityTypes, icons, getAlertIcon,
} from '../../../constants';
import { constructEtaValue } from '../../../helpers/shipment-helper';
import { shipments as demoShipments, facilities as demoFacilities } from '../../../data/shipments-demo';

export default {
  name: 'ShipmentMap',
  components: {
    MapWithPopup,
    Loader,
  },
  props: {
    shipmentId: { type: String, required: true },
  },
  computed: {
    isShipmentsDemo() { return this.$root.workspaceFeatures.shipmentsDemo; },
    isLoading() { return this.$store.state.shipments.locations.isLoading; },
    isMoreData() {
      // we load all locations at once, so there is no more data
      return false;
    },
    shipment() {
      if (this.isShipmentsDemo) {
        const shipment = demoShipments.find((s) => s.id === this.shipmentId) || {};
        return {
          ...shipment,
          origin: {
            ...shipment.origin,
            where: {
              ...shipment.origin.where,
              facility: demoFacilities
                .find((facility) => facility.id === shipment.origin.where.facility.id),
            },
          },
          destination: {
            ...shipment.destination,
            where: {
              ...shipment.destination.where,
              facility: demoFacilities
                .find((facility) => facility.id === shipment.destination.where.facility.id),
            },
          },
        };
      }

      return this.$store.state.shipments.shipments.find(({ id }) => id === this.shipmentId) || {};
    },
    locations() {
      if (this.isShipmentsDemo) {
        return (this.shipment.locations || {}).items || [];
      }
      return this.$store.state.shipments.locations.shipmentsData
        .find((d) => d.shipmentId === this.shipmentId)?.items || [];
    },
    isOriginFacility() {
      return this.shipment && this.shipment.origin
        && this.shipment.origin.where
        && this.shipment.origin.where.facility;
    },
    originHasCoordinates() {
      const orig = this.shipment.origin;
      return orig && orig.where
        && orig.where.coordinate
        && orig.where.coordinate.latitude
        && orig.where.coordinate.longitude;
    },
    origin() {
      if (this.originHasCoordinates) {
        return {
          latitude: this.shipment.origin.where.coordinate.latitude,
          longitude: this.shipment.origin.where.coordinate.longitude,
          properties: {
            itemType: mapItemTypes.origin,
            type: mapEntityTypes.coordinate,
            icon: icons.origin,
            name: this.shipment.origin.where.name || 'Origin',
            eta: constructEtaValue(
              (this.shipment.origin.when || {}).estimated,
              this.shipment.origin,
            ),
          },
        };
      }

      if (this.isOriginFacility) {
        return {
          ...this.shipment.origin.where.facility.location,
          properties: {
            itemType: mapItemTypes.origin,
            type: mapEntityTypes.facility,
            icon: icons.facility,
            name: this.shipment.origin.where.facility.name,
            eta: constructEtaValue(
              (this.shipment.origin.when || {}).estimated,
              this.shipment.origin,
            ),
          },
        };
      }

      return null;
    },
    isDestinationFacility() {
      return this.shipment && this.shipment.destination
        && this.shipment.destination.where
        && this.shipment.destination.where.facility;
    },
    destinationHasCoordinates() {
      const dest = this.shipment.destination;
      return dest && dest.where
        && dest.where.coordinate
        && dest.where.coordinate.latitude
        && dest.where.coordinate.longitude;
    },
    destination() {
      if (this.destinationHasCoordinates) {
        return {
          latitude: this.shipment.destination.where.coordinate.latitude,
          longitude: this.shipment.destination.where.coordinate.longitude,
          properties: {
            itemType: mapItemTypes.destination,
            type: mapEntityTypes.coordinate,
            icon: icons.destination,
            name: this.shipment.destination.where.name || 'Destination',
            eta: constructEtaValue(
              (this.shipment.destination.when || {}).estimated,
              this.shipment.destination,
            ),
          },
        };
      }

      if (this.isDestinationFacility) {
        return {
          ...this.shipment.destination.where.facility.location,
          properties: {
            itemType: mapItemTypes.destination,
            type: mapEntityTypes.facility,
            icon: icons.facility,
            name: this.shipment.destination.where.facility.name,
            eta: constructEtaValue(
              (this.shipment.destination.when || {}).estimated,
              this.shipment.destination,
            ),
          },
        };
      }

      return null;
    },
    alerts() {
      return this.locations
        .filter((loc) => loc.isAlert)
        .map((loc) => {
          const alert = this.shipment.alerts.find((a) => a.timestamp === loc.timestamp);
          return {
            ...loc,
            properties: {
              type: mapItemTypes.alert,
              name: alert.message,
              icon: getAlertIcon(alert.category, alert.type),
              iconColor: 'warning',
            },
          };
        });
    },
  },
  watch: {
    shipmentId() {
      this.getData();
    },
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      if (!this.isShipmentsDemo) {
        this.$store.dispatch('shipments/locations/getAllShipmentLocations', { shipmentId: this.shipmentId, reset: true });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.shipment-map-container {
  width: 100%;
  height: 100%;
  position: relative;
}
.no-data-message {
  padding: 20px;
}
</style>
